/* eslint-disable i18next/no-literal-string, @nx/workspace-i18n-no-hardcoded-strings */
import { useEffect, useState } from 'react'

import styled from '@emotion/styled'

import { Drawer, spacing, Text } from '@cash-design-system/react'

import { Money, Portal, PortalIdentifier } from '../../src/components'
import { useLoan } from '../../src/state'

const Container = styled.div`
  padding-left: ${spacing.xs};
  padding-right: ${spacing.xs};
`
const TitleContainer = styled.div`
  margin-bottom: ${spacing.m};
`
const Td = styled.td`
  border: 1px solid black;
  padding: ${spacing.xxs};
`

const Table = styled.table`
  border-collapse: collapse;
  margin-bottom: ${spacing.l};
`

const FeeTable = styled(Table)`
  margin-bottom: ${spacing.xl};
`

const BulletListItem = styled.li`
  padding-left: ${spacing.xs};
`

const TdSectionHeader = styled(Td)`
  text-align: center;
`

const ColorOverride = styled.div`
  [role='dialog'] {
    background-color: #ffffff;
  }
`

const Link = styled.a`
  color: #3399ff;
  text-decoration: underline;
`

interface AccountOpeningDisclosureProps {
  open: boolean
  onClose: () => void
}

export function AccountOpeningDisclosure({ open, onClose }: AccountOpeningDisclosureProps) {
  const loanData = useLoan()
  const [height, setHeight] = useState(0)
  useEffect(() => {
    const updateHeight = () => {
      setHeight(window.innerHeight * 0.75)
    }

    updateHeight()

    window.addEventListener('resize', updateHeight)

    return () => window.removeEventListener('resize', updateHeight)
  }, [])

  if (!loanData || !loanData.tila_data) {
    return null
  }
  const tilaData = loanData.tila_data

  return (
    <Portal id={PortalIdentifier.Modal}>
      <ColorOverride>
        <Drawer show={open} onClose={onClose}>
          <Container style={{ height }}>
            <TitleContainer>
              <Text typography="hero" textAlign="center">
                CASH APP PAY OVER TIME
              </Text>
              <Text typography="hero" textAlign="center">
                PRICING INFORMATION
              </Text>
            </TitleContainer>
            <Table>
              <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '75%' }} />
              </colgroup>
              <tbody>
                <tr>
                  <TdSectionHeader colSpan={2}>
                    <Text typography="titleBarPageTitle">INTEREST RATES AND INTEREST CHARGES</Text>
                  </TdSectionHeader>
                </tr>
                <tr>
                  <Td>
                    <Text typography="labelMedium">Annual Percentage Rate (APR)</Text>
                  </Td>
                  <Td>
                    <Text typography="body">{tilaData.apr_percentage}</Text>
                  </Td>
                </tr>
                <tr>
                  <Td>
                    <Text typography="labelMedium">Penalty APR and When It Applies</Text>
                  </Td>
                  <Td>
                    <Text typography="body">None</Text>
                  </Td>
                </tr>
                <tr>
                  <Td>
                    <Text typography="labelMedium">How to Avoid Paying Interest on Purchases</Text>
                  </Td>
                  <Td>
                    <Text typography="body">N/A</Text>
                  </Td>
                </tr>
                <tr>
                  <Td>
                    <Text typography="labelMedium">Tips from the Consumer Financial Protection Bureau</Text>
                  </Td>
                  <Td>
                    <Text typography="body">
                      To learn more about factors to consider when applying for or using financial products, visit the
                      website of the Consumer Financial Protection Bureau at{' '}
                      <Link target="_blank" rel="noreferrer" href="https://www.consumerfinance.gov/learnmore">
                        https://www.consumerfinance.gov/learnmore
                      </Link>
                    </Text>
                  </Td>
                </tr>
              </tbody>
            </Table>
            <FeeTable>
              <colgroup>
                <col style={{ width: '25%' }} />
                <col style={{ width: '75%' }} />
              </colgroup>
              <tbody>
                <tr>
                  <TdSectionHeader colSpan={2}>
                    <Text typography="titleBarPageTitle">FEES</Text>
                  </TdSectionHeader>
                </tr>
                <tr>
                  <Td>
                    <Text typography="labelMedium">Penalty Fee:</Text>
                    <br />
                    <ul>
                      <BulletListItem>
                        <Text typography="body">• Late Fee</Text>
                      </BulletListItem>
                    </ul>
                  </Td>
                  <Td>
                    <Text typography="labelMedium">
                      Up to <Money value={tilaData.late_fee_money} />
                    </Text>
                  </Td>
                </tr>
                <tr>
                  <Td>
                    <Text typography="labelMedium">Origination Fee</Text>
                  </Td>
                  <Td>
                    <Text typography="body">{tilaData.setup_fee_percentage} of the total loan amount</Text>
                  </Td>
                </tr>
              </tbody>
            </FeeTable>
            <Text typography="helpText">
              How We Will Calculate Your Balance: We use a method called the Adjusted Balance. We calculate your balance
              by deducting payments and credits made during the billing cycle from the outstanding balance at the
              beginning of the billing cycle.
            </Text>
            <br />
            <Text typography="helpText">
              Billing Rights: Please refer to your loan agreement for information on your billing error rights.
            </Text>
            <br />
          </Container>
        </Drawer>
      </ColorOverride>
    </Portal>
  )
}
